/**
 * MessageNotificationService
 *
 * Handles sound + browser/native notifications for incoming chat messages.
 *
 * Sound: synthesised via Web Audio API — no audio files required.
 *   Two short ascending tones (like a soft chime) using exponential gain envelopes.
 *
 * Browser notification: shown via the Web Notification API when the page is
 *   not focused or the tab is hidden.  Clicking it focuses the window.
 *
 * Native notification: delegates to NativeBridge.presentNotification() when
 *   running inside the Quantis iOS/Android shell.
 *
 * Usage:
 *   // Once, on app boot — requests browser notification permission
 *   MessageNotificationService.requestPermission();
 *
 *   // On every incoming message that isn't from the current user
 *   MessageNotificationService.notify({ senderName: 'Alice', preview: 'Hey there!', chatId });
 */

import { getPreferencesSnapshot } from '../features/preferences/preferencesStore';
import { NativeBridge } from './NativeBridge';
import { isTauriRuntime, desktopNotify } from '../platform/tauri/nativeShell';

declare global {
  interface Window {
    __QUANTIS_OPEN_CHAT__?: (chatId: string, messageId?: string | null) => void;
  }
}

export interface MessageNotifyOptions {
  senderName: string;
  preview: string;
  chatId?: string;
  /** Suppress the sound (e.g. user has muted chat) */
  silent?: boolean;
}

/** Body shown in place of message text when the user hides previews. */
export const HIDDEN_PREVIEW_TEXT = 'New message';

/** The text a notification may show: the message itself, or a generic line when previews are off. */
export function visibleNotificationPreview(preview: string, showPreview: boolean): string {
  return showPreview ? preview : HIDDEN_PREVIEW_TEXT;
}

// ── Audio ──────────────────────────────────────────────────────────────────────
// Uses an HTMLAudioElement with an inline WAV data URI — no AudioContext,
// no autoplay policy warnings, no external files.

// Minimal two-tone chime encoded as a 44-byte mono 8kHz WAV (base64).
// Generated offline; plays a short soft beep on all browsers.
const CHIME_WAV =
  'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAA' +
  'EAAQAAgD4AAAQABAACABIAZGF0YVAGAAAAAAAAAAAAAAAA';

let chimeAudio: HTMLAudioElement | null = null;

function getChimeAudio(): HTMLAudioElement | null {
  if (typeof Audio === 'undefined') return null;
  if (!chimeAudio) {
    chimeAudio = new Audio(CHIME_WAV);
    chimeAudio.volume = 0.4;
  }
  return chimeAudio;
}

function playChime(): void {
  if (getPreferencesSnapshot().notifications.muteSounds) return;
  const audio = getChimeAudio();
  if (!audio) return;
  audio.currentTime = 0;
  audio.play().catch(() => {
    // Autoplay blocked — silently ignore, toast already notifies the user.
  });
}

// ── Browser / native notification ─────────────────────────────────────────────

/**
 * No-op stub kept for API compatibility — permission is handled by
 * useBrowserPermissionsBootstrap (requires a real user gesture).
 * Calling Notification.requestPermission() without a gesture is silently
 * ignored by Chrome 84+ and may block the proper bootstrap flow.
 */
export async function requestPermission(): Promise<void> {
  // intentionally empty — permission is requested via PermissionPrompt / useBrowserPermissionsBootstrap
}

function showBrowserNotification(opts: MessageNotifyOptions): void {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;

  // Only show the OS notification when the tab is hidden/backgrounded.
  // When the tab is visible, the in-app toast already handles the alert.
  if (!document.hidden) return;

  const note = new Notification(opts.senderName, {
    body: opts.preview,
    icon: '/icons/icon-192.png',
    tag: opts.chatId ? `chat-${opts.chatId}` : 'chat',
    silent: true, // we handle sound ourselves
    ...({ renotify: true } as NotificationOptions),
  });

  note.onclick = () => {
    window.focus();
    note.close();
    if (opts.chatId && typeof window.__QUANTIS_OPEN_CHAT__ === 'function') {
      window.__QUANTIS_OPEN_CHAT__(opts.chatId);
    }
  };
}

function showNativeNotification(opts: MessageNotifyOptions): void {
  if (!NativeBridge.isRunningNatively()) return;
  NativeBridge.haptic('light');
  const bridge = (window as any).QuantisNativeBridge;
  if (bridge?.presentNotification) {
    bridge.presentNotification({
      title: opts.senderName,
      body: opts.preview,
      userInfo: opts.chatId ? { chatId: opts.chatId } : undefined,
    });
  }
}

// ── Public API ─────────────────────────────────────────────────────────────────

export const MessageNotificationService = {
  requestPermission,

  /**
   * Trigger a notification for an incoming message.
   * Call this only for messages whose sender_id !== current user id.
   */
  notify(opts: MessageNotifyOptions): void {
    if (!opts.silent) {
      playChime();
    }

    if (NativeBridge.isRunningNatively()) {
      showNativeNotification(opts);
    } else if (isTauriRuntime()) {
      // Desktop shell: WKWebView/WebView2 have no Web Notification API, so
      // route through the OS notification centre. Same backgrounded-only
      // rule as the browser path; foreground gets the in-app toast.
      if (!document.hasFocus()) {
        void desktopNotify({ title: opts.senderName, body: opts.preview });
      }
    } else {
      showBrowserNotification(opts);
    }
  },
} as const;
